import { makeAutoObservable } from "mobx";
import { isEmpty } from "lodash";
import { parseDigits } from "libphonenumber-js";
import { CompanyRole } from "@/types/CompanyInfo.ts";
import { NewCompanyRoleRequest, settingsRoleTypes } from "@/types/Settings.ts";
import { PhoneInputModel } from "@/components-v2/PhoneInput.tsx";
import { getFullName, splitName, validateName } from "@/utils/Roles.ts";
import { getRoleTypeLabel, RoleType } from "@/types/enums/RoleType.ts";
import { isBlank } from "@/utils/strings.ts";
import {
  createEmailField,
  LocalDateFieldModel,
  RadioFieldModel,
  StringFieldModel,
} from "@/common/commonForms.ts";
import { norway } from "@/utils/countries.ts";
import { dNumberValidator, maskNationalId } from "@/utils/other.ts";
import { EntityValueFormModel } from "@/customFields/customFieldsDomain.ts";
import { TMessage } from "@/i18n/tt.ts";
import { plusDays, today, toLocalDateISOString } from "@/utils/Dates.ts";
import { AccountRoleType } from "@/companyRoles/accountRoleTypeApiClient.ts";

export enum RolesDialogState {
  CLOSE = "close",
  CREATE = "create",
  EDIT = "edit",
  REMOVE = "remove",
  REVOKE = "revoke",
}

export type CloseCompanySettingsDialog = {
  state: DialogState.CLOSE;
};

export enum DialogState {
  CLOSE = "close",
  REVOKE = "revoke",
}

export type RoleCustomFields = {
  title: TMessage;
  models: EntityValueFormModel[];
};

export type RoleFormSettingsDialog = {
  state: RolesDialogState.CREATE | RolesDialogState.EDIT;
  form: RoleForm;
  showPeriod: boolean;
  showResidentialAddress: boolean;
  customFields?: RoleCustomFields;
};

export type RemoveCompanySettingsDialog = {
  state: RolesDialogState.REMOVE;
  companyroleId: number;
  onSuccess?: () => void;
  isResolving?: boolean;
};

export type CompanyRolesDialog =
  | CloseCompanySettingsDialog
  | RoleFormSettingsDialog
  | RemoveCompanySettingsDialog;

export type RolesDialogsProps = {
  settingsDialog: CompanyRolesDialog;
  isCompanyRoleLoading: boolean;
  onCloseDialog: () => void;
  onSubmitDialog: () => void;
  companyName: string;
};

const dayAfter = (date?: string): string | undefined =>
  date && toLocalDateISOString(plusDays(date, 1));

export type RoleChoice = RoleType | AccountRoleType;

export type RoleChoiceRequest = { role?: RoleType; accountRoleTypeId?: string };

export const roleChoiceOf = (role: {
  role: RoleType;
  accountRoleType?: AccountRoleType;
}): RoleChoice => role.accountRoleType ?? role.role;

export const roleChoices = (accountRoleTypes: AccountRoleType[]): RoleChoice[] => [
  ...settingsRoleTypes,
  ...accountRoleTypes,
];

export const roleChoiceId = (choice: RoleChoice): string =>
  typeof choice === "object" ? choice.id : choice;

export const roleChoiceLabel = (choice: RoleChoice): TMessage =>
  typeof choice === "string" ? getRoleTypeLabel(choice) : { translated: choice.name };

export const roleChoiceRequest = (choice?: RoleChoice): RoleChoiceRequest =>
  typeof choice === "object" ? { accountRoleTypeId: choice.id } : { role: choice };

export const roleChoiceField = (
  value: RoleChoice | undefined,
  accountRoleTypes: AccountRoleType[],
): RadioFieldModel<RoleChoice | undefined> =>
  new RadioFieldModel<RoleChoice | undefined>({
    required: true,
    value,
    options: roleChoices(accountRoleTypes),
    idFn: (choice) => (choice ? roleChoiceId(choice) : ""),
    labelFn: (choice) => (choice ? roleChoiceLabel(choice) : { key: "settings.selectRole" }),
  });

export class RoleForm {
  companyroleId?: number;
  firstName: StringFieldModel;
  lastName: StringFieldModel;
  middleName: StringFieldModel;
  name: StringFieldModel;
  roleTypeSelection: RadioFieldModel<RoleChoice | undefined>;
  email: StringFieldModel;
  phoneNumber: PhoneInputModel;
  socialSecurityNumber: StringFieldModel;
  nationality: StringFieldModel;
  dNumber: StringFieldModel;
  validFrom: LocalDateFieldModel;
  validTo: LocalDateFieldModel;
  addressLine1: StringFieldModel;
  addressLine2: StringFieldModel;
  postalCode: StringFieldModel;
  city: StringFieldModel;
  addressCountry: StringFieldModel;
  canEditSocialSecurityNumber: boolean;
  personReference?: string;

  constructor(
    role?: Partial<CompanyRole>,
    roleChoice?: RoleChoice,
    accountRoleTypes: AccountRoleType[] = [],
    personReference?: string,
  ) {
    makeAutoObservable(this, {}, { autoBind: true });
    this.companyroleId = role?.companyroleId;
    this.firstName = new StringFieldModel({ value: role?.firstName, required: true });
    this.lastName = new StringFieldModel({ value: role?.lastName, required: true });
    this.middleName = new StringFieldModel({ value: role?.middleName });
    this.name = new StringFieldModel({
      value: getFullName(role),
      required: true,
      validator: validateName,
    });
    this.email = createEmailField({ value: role?.email, required: false });
    this.phoneNumber = new PhoneInputModel({
      value: role?.phoneNumber,
      required: false,
    });
    const isExisting = role?.companyroleId !== undefined;
    this.socialSecurityNumber = new StringFieldModel({
      value: isExisting
        ? maskNationalId(role?.socialSecurityNumber ?? "")
        : role?.socialSecurityNumber,
    });
    this.nationality = new StringFieldModel({ value: role ? role.nationality : norway });
    this.dNumber = new StringFieldModel({ value: role?.dNumber, validator: dNumberValidator });
    this.roleTypeSelection = roleChoiceField(
      roleChoice ?? role?.accountRoleType ?? role?.role,
      accountRoleTypes,
    );
    const todayISO = toLocalDateISOString(today());
    const validFrom = role?.validFrom ?? todayISO;
    this.validFrom = new LocalDateFieldModel({
      value: validFrom,
      required: true,
      maxDate: todayISO,
      onChange: (value) => {
        this.validTo.minDate = dayAfter(value);
        this.validTo.setAndCommitInputValue(this.validTo.inputValue);
      },
    });
    this.validTo = new LocalDateFieldModel({ value: role?.validTo, minDate: dayAfter(validFrom) });
    const address = role?.residentialAddress;
    this.addressLine1 = new StringFieldModel({ value: address?.addressLine1 });
    this.addressLine2 = new StringFieldModel({ value: address?.addressLine2 });
    this.postalCode = new StringFieldModel({ value: address?.postalCode });
    this.city = new StringFieldModel({ value: address?.city });
    this.addressCountry = new StringFieldModel({ value: role ? address?.country : norway });
    this.canEditSocialSecurityNumber = isExisting || isBlank(role?.socialSecurityNumber ?? "");
    this.personReference = personReference;
  }

  get displayedSocialSecurityNumber(): string {
    return this.canEditSocialSecurityNumber
      ? this.socialSecurityNumber.inputValue
      : maskNationalId(this.socialSecurityNumber.inputValue);
  }

  get showDNumber(): boolean {
    return !isBlank(this.nationality.inputValue) && this.nationality.inputValue !== norway;
  }

  setNationality(country: string) {
    this.nationality.setAndCommitInputValue(country);
    if (!this.showDNumber) {
      this.dNumber.clear();
    }
  }

  setAddressCountry(country: string) {
    this.addressCountry.setAndCommitInputValue(country);
  }

  get hasResidentialAddress(): boolean {
    return [this.addressLine1, this.addressLine2, this.postalCode, this.city].some(
      (field) => !field.isEmpty,
    );
  }

  validateOnCreate(): boolean {
    this.name.validate();
    this.phoneNumber.validate();
    this.email.validate();
    this.roleTypeSelection.validate();
    this.socialSecurityNumber.validate();
    this.dNumber.validate();
    this.validatePeriod();
    this.validateResidentialAddress();
    return this.isValidOnCreate;
  }

  validateOnEdit(): boolean {
    this.phoneNumber.validate();
    this.email.validate();
    this.roleTypeSelection.validate();
    this.dNumber.validate();
    this.validatePeriod();
    this.validateResidentialAddress();
    return this.isValidOnEdit;
  }

  private validatePeriod() {
    this.validFrom.commit();
    this.validFrom.validate();
    this.validTo.commit();
    this.validTo.validate();
  }

  private validateResidentialAddress() {
    const isRequired = this.hasResidentialAddress;
    [this.addressLine1, this.city].forEach((field) => {
      field.setRequired(isRequired);
      field.validate();
    });
  }

  get isValidOnCreate() {
    return (
      this.name.isValid &&
      this.phoneNumber.isValid &&
      this.email.isValid &&
      this.roleTypeSelection.isValid &&
      this.socialSecurityNumber.isValid &&
      this.dNumber.isValid &&
      this.validFrom.isValid &&
      this.validTo.isValid &&
      this.addressLine1.isValid &&
      this.city.isValid
    );
  }

  get isValidOnEdit() {
    return (
      this.phoneNumber.isValid &&
      this.email.isValid &&
      this.roleTypeSelection.isValid &&
      this.dNumber.isValid &&
      this.validFrom.isValid &&
      this.validTo.isValid &&
      this.addressLine1.isValid &&
      this.city.isValid &&
      this.addressCountry.isValid
    );
  }

  applyPeriod(): Pick<NewCompanyRoleRequest, "validFrom" | "validTo"> {
    return { validFrom: this.validFrom.toISOString(), validTo: this.validTo.toISOString() };
  }

  applyResidentialAddress(): Pick<NewCompanyRoleRequest, "residentialAddress"> {
    return {
      residentialAddress: {
        addressLine1: this.addressLine1.inputValue,
        addressLine2: this.addressLine2.inputValue,
        postalCode: this.postalCode.inputValue,
        city: this.city.inputValue,
        country: this.addressCountry.inputValue,
      },
    };
  }

  applyForm(): NewCompanyRoleRequest {
    const { firstName, middleName, lastName } = splitName(this.name.inputValue);
    const phoneNumber = isEmpty(this.phoneNumber.number.trim())
      ? undefined
      : {
          value: parseDigits(this.phoneNumber.number),
          countryCode: this.phoneNumber.selectedCountry?.callingCode.trim(),
        };
    return {
      companyroleId: this.companyroleId,
      firstName: firstName,
      lastName: lastName,
      middleName: middleName,
      phoneNumber: phoneNumber,
      email: this.email?.inputValue,
      ...roleChoiceRequest(this.roleTypeSelection.value),
      socialSecurityNumber: this.socialSecurityNumber.inputValue,
      personReference: this.personReference,
      nationality: this.nationality.inputValue,
      dNumber: this.dNumber.inputValue,
    };
  }
}
